import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import FolderTree from './FolderTree';
import FormCard from './FormCard';

const FolderManager = () => {
  const { t } = useTranslation();
  const { id: urlFormId } = useParams();
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editFolderDialogOpen, setEditFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editFolderName, setEditFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [formId, setFormId] = useState(urlFormId ? parseInt(urlFormId, 10) : Math.floor(Math.random() * 1000000));
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [data, setData] = useState({ folders: [], forms: [] });
  const [loading, setLoading] = useState(true);
  const [selectedForms, setSelectedForms] = useState([]); // Seçilen formlar
const [forms, setForms] = useState([]);
  const navigate = useNavigate();
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);
  
  // Karakter sınırı
  const MAX_FOLDER_NAME_LENGTH = 50;

  const buildTree = (items) => {
    if (!Array.isArray(items)) return [];
    
    const processItem = (item) => {
      return {
        ...item,
        id: item.id,
        title: item.title,
        parentId: item.parentId,
        children: Array.isArray(item.detail) ? item.detail.map(child => processItem(child)) : []
      };
    };

    // Sadece üst seviye (parentId = 0) klasörleri al ve işle
    return items
      .filter(item => item.parentId === 0)
      .map(item => processItem(item));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('=== Klasörler Getiriliyor ===');
      const response = await axios.get("https://app.hhoms.com/api/form/Categories/list", {
        headers: { ApplicationToken: appToken, User: userData.id }
      });
      
      console.log('Klasör API Yanıtı:', response.data);
      
      if (response.data && response.data.formCategories) {
        const folders = response.data.formCategories;
        console.log('İşlenecek klasörler:', folders);
        
        // Klasör ağacını oluştur
        const folderTree = buildTree(folders);
        console.log('Oluşturulan klasör ağacı:', folderTree);
        
        setData(prevData => ({ 
          ...prevData, 
          folders: folderTree,
          forms: [] 
        }));
        
        if (folders.length === 0) {
          toast.warn("Klasör bulunamadı.");
        }
      } else {
        toast.error("Klasör verisi alınamadı");
      }
    } catch (error) {
      console.error("Klasörler yüklenirken hata:", error.response ? error.response.data : error.message);
      toast.error("Klasörler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    try {
      setLoading(true);
      console.log('=== Formlar Getiriliyor ===');
      
      // Hem formları (filetype 0) hem de tabloları (filetype 1) getir
      const fileTypes = [0, 1];
      let allForms = [];
      
      for (const fileType of fileTypes) {
        try {
          const response = await axios.get(`https://app.hhoms.com/api/form/Libraries/list/0/${fileType}`, {
            headers: { 
              ApplicationToken: appToken, 
              User: userData.id,
              'Content-Type': 'application/json'
            }
          });
          
          console.log(`FileType ${fileType} API Yanıtı:`, response.data);
          
          // API yanıtını kontrol et
          let forms = [];
          if (response.data && response.data.formLibraries) {
            forms = response.data.formLibraries;
          } else if (Array.isArray(response.data)) {
            forms = response.data;
          } else if (response.data && response.data.forms) {
            forms = response.data.forms;
          }
          
          // Formları filetype ile işaretle ve birleştir
          const formsWithType = forms.map(form => ({
            ...form,
            fileType: fileType
          }));
          
          allForms = [...allForms, ...formsWithType];
        } catch (error) {
          console.error(`FileType ${fileType} için hata:`, error);
          // Bir filetype başarısız olursa diğerini denemeye devam et
        }
      }
      
      // Tüm formları işle
      processAndSetForms(allForms);
      
    } catch (error) {
      console.error("Formlar yüklenirken hata:", error.response?.data || error.message);
      toast.error("Formlar yüklenirken bir hata oluştu: " + (error.response?.data?.message || error.message));
      setData(prevData => ({ ...prevData, forms: [] }));
    } finally {
      setLoading(false);
    }
  };
  
  // Formları işleyip state'e kaydeden yardımcı fonksiyon
  const processAndSetForms = (formLibraries) => {
    if (!Array.isArray(formLibraries)) {
      console.error('formLibraries bir dizi değil:', formLibraries);
      setData(prevData => ({ ...prevData, forms: [] }));
      return;
    }
    
    if (formLibraries.length === 0) {
      console.log('Hiç form bulunamadı');
      setData(prevData => ({ ...prevData, forms: [] }));
      return;
    }
    
    console.log('İşlenecek formlar:', formLibraries);
    
    // Formları işle ve state'e kaydet
    const organizedForms = formLibraries.map(form => ({
      id: form.id,
      formId: form.formId || form.id, // formId yoksa id'yi kullan
      title: form.title || 'İsimsiz Form',
      description: form.description || '',
      parentId: form.parentId || 0,
      folderId: form.parentId || 0,
      fileType: form.fileType !== undefined ? form.fileType : 0, // Default olarak form (0)
      isActive: form.isActive !== undefined ? form.isActive : true,
      records: form.records || 0,
      createDate: form.createDate,
      editDate: form.editDate,
      updatedAt: form.updatedAt || new Date().toISOString()
    }));
    
    console.log('İşlenmiş formlar:', organizedForms);
    console.log('Örnek form verisi:', organizedForms[0]);
    console.log(`Toplam: ${organizedForms.length} (${organizedForms.filter(f => f.fileType === 0).length} form, ${organizedForms.filter(f => f.fileType === 1).length} tablo)`);
    setData(prevData => ({ ...prevData, forms: organizedForms }));
  };

  // Klasör ağacını düzleştiren yardımcı fonksiyon
  const flattenFolderTree = (folders, level = 0, path = "") => {
    if (!folders || !Array.isArray(folders)) return [];
    
    let result = [];
    
    folders.forEach(folder => {
      // Mevcut klasörü ekle
      const currentPath = path ? `${path} > ${folder.title}` : folder.title;
      result.push({
        ...folder,
        level,
        path: currentPath,
        isParent: folder.children && folder.children.length > 0
      });
      
      // Alt klasörleri ekle
      if (folder.children && folder.children.length > 0) {
        result = result.concat(flattenFolderTree(folder.children, level + 1, currentPath));
      }
    });
    
    return result;
  };

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchData();
      // fetchForms'u kaldırdık, sadece klasörleri yeniliyoruz
    } catch (error) {
      setErrorMessage('Veriler yenilenirken hata oluştu');
    } finally {
      setRefreshing(false);
    }
  }, []);

  const handleDeleteForm = async (formId) => {
    if (window.confirm('Bu formu silmek istediğinizden emin misiniz?')) {
      try {
        await axios.delete(`https://app.hhoms.com/api/form/Libraries/${formId}`, {
          headers: { ApplicationToken: appToken, User: userData.id }
        });
        
        toast.success('Form başarıyla silindi');
        
        // Seçili klasördeki formları yeniden yükle
        if (selectedFolderId) {
          await handleFolderSelect({ id: selectedFolderId });
        } else {
          await fetchForms(); // Tüm formları yeniden yükle
        }
      } catch (error) {
        console.error('Form silme hatası:', error);
        toast.error('Form silinirken hata oluştu');
      }
    }
  };
  const handleDuplicateForm = async (form) => {
    try {
      console.log("Form çoğaltılıyor:", form);
      
      const sourceFormId = form.id || form.formId;
      
      if (!sourceFormId) {
        toast.error("Form ID bulunamadı, çoğaltma işlemi yapılamıyor.");
        return;
      }
      
      const formDetailResponse = await axios.get(`https://app.hhoms.com/api/form/Libraries/${sourceFormId}`, {
        headers: {
          ApplicationToken: appToken,
          User: userData.id,
          "Content-Type": "application/json",
        },
        timeout: 10000
      });
      
      const formDetail = formDetailResponse.data;
      let formContentValue = null;
      
      if (formDetail && formDetail.formContent && formDetail.formContent !== "null") {
        formContentValue = formDetail.formContent;
      } else if (form && form.formContent && form.formContent !== "null") {
        formContentValue = form.formContent;
      }
      
      if (formContentValue && typeof formContentValue !== 'string') {
        formContentValue = JSON.stringify(formContentValue);
      }
      
      if (!formContentValue || formContentValue === "null") {
        const basicFormContent = {
          title: form.title || "Form",
          description: form.description || "",
          fields: [],
          settings: {
            fieldSize: "medium",
            backgroundColor: "#ffffff",
            titleColor: "#000000",
            descriptionColor: "#666666",
            submitButtonColor: "#1976d2",
            submitButtonText: "Gönder",
            submitButtonTextColor: "#ffffff"
          }
        };
        formContentValue = JSON.stringify(basicFormContent);
      }
      
      const duplicatedForm = {
        parentId: formDetail.parentId || form.parentId || "0",
        title: `${formDetail.title || form.title || 'Form'} - Kopya`,
        formContent: formContentValue,
        isActive: true
      };
      
      const response = await axios.post(`https://app.hhoms.com/api/form/Libraries`, duplicatedForm, {
        headers: {
          ApplicationToken: appToken,
          User: userData.id,
          "Content-Type": "application/json",
        },
        timeout: 15000
      });
      
      if (response.data) {
        // Yeni oluşturulan formu listeye ekle
        const newForm = {
          id: response.data.id || response.data.formId,
          formId: response.data.formId || response.data.id,
          title: duplicatedForm.title,
          description: form.description || '',
          parentId: duplicatedForm.parentId,
          folderId: duplicatedForm.parentId,
          isActive: true,
          updatedAt: new Date().toISOString()
        };

        setData(prevData => ({
          ...prevData,
          forms: [...prevData.forms, newForm]
        }));

        toast.success("Form başarıyla çoğaltıldı!");
      }
    } catch (error) {
      console.error("Form çoğaltma hatası:", error);
      
      // Alternatif yöntem deneyelim
      try {
        const sourceFormId = form.id || form.formId;
        const response = await axios.post(`https://app.hhoms.com/api/form/Libraries/duplicate/${sourceFormId}`, {}, {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            "Content-Type": "application/json",
          }
        });
        
        if (response.data) {
          // Alternatif yöntemle oluşturulan formu listeye ekle
          const newForm = {
            id: response.data.id || response.data.formId,
            formId: response.data.formId || response.data.id,
            title: `${form.title || 'Form'} - Kopya`,
            description: form.description || '',
            parentId: form.parentId || "0",
            folderId: form.parentId || "0",
            isActive: true,
            updatedAt: new Date().toISOString()
          };

          setData(prevData => ({
            ...prevData,
            forms: [...prevData.forms, newForm]
          }));

          toast.success("Form başarıyla çoğaltıldı!");
        }
      } catch (altError) {
        console.error("Alternatif form çoğaltma hatası:", altError);
        if (error.response) {
          toast.error(`Form çoğaltılırken hata oluştu: ${error.response.status} - ${error.response.data?.message || 'Sunucu hatası'}`);
        } else if (error.request) {
          toast.error("Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.");
        } else {
          toast.error(`Form çoğaltılırken hata oluştu: ${error.message}`);
        }
      }
    }
  };
  
  const handleFolderSelect = async (folder) => {
    console.log("Seçilen klasör:", folder);
    setSelectedFolderId(folder.id);
    setLoading(true);

    try {
      // Hem formları (filetype 0) hem de tabloları (filetype 1) getir
      const fileTypes = [0, 1];
      let allForms = [];
      
      for (const fileType of fileTypes) {
        try {
          const response = await axios.get(`https://app.hhoms.com/api/form/Libraries/list/${folder.id}/${fileType}`, {
            headers: { 
              ApplicationToken: appToken, 
              User: userData.id,
              'Content-Type': 'application/json'
            }
          });

          console.log(`FileType ${fileType} yanıtı:`, response.data);

          // API yanıtını kontrol et ve formları işle
          let forms = [];
          if (response.data && response.data.formLibraries) {
            forms = response.data.formLibraries;
          } else if (Array.isArray(response.data)) {
            forms = response.data;
          } else if (response.data && response.data.forms) {
            forms = response.data.forms;
          }

          // Formları filetype ile işaretle ve birleştir
          const formsWithType = forms.map(form => ({
            ...form,
            fileType: fileType
          }));
          
          allForms = [...allForms, ...formsWithType];
        } catch (error) {
          console.error(`FileType ${fileType} için hata:`, error);
          // Bir filetype başarısız olursa diğerini denemeye devam et
        }
      }

      // Tüm formları işle ve state'e kaydet
      const processedForms = allForms.map(form => ({
        id: form.id,
        formId: form.formId || form.id,
        title: form.title || 'İsimsiz Form',
        description: form.description || '',
        parentId: form.parentId || folder.id,
        folderId: form.parentId || folder.id,
        fileType: form.fileType, // Form tipini ekle
        isActive: form.isActive !== undefined ? form.isActive : true,
        updatedAt: form.updatedAt || new Date().toISOString()
      }));

      setData(prevData => ({
        ...prevData,
        selectedFolderTitle: folder.title,
        forms: processedForms
      }));

      if (processedForms.length === 0) {
        toast.info(`"${folder.title}" klasöründe henüz form bulunmuyor.`);
      } else {
        console.log(`${processedForms.length} form/tablo yüklendi (${processedForms.filter(f => f.fileType === 0).length} form, ${processedForms.filter(f => f.fileType === 1).length} tablo)`);
      }
    } catch (error) {
      console.error("Formlar yüklenirken hata:", error.response?.data || error.message);
      toast.error("Formlar yüklenirken bir hata oluştu: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };
  const fetchFolderAndForms = async (folder) => {
    try {
      const request1 = axios.get(`https://app.hhoms.com/api/form/Libraries/list/${folder.id}`, {
        headers: { ApplicationToken: appToken, User: userData.id }
      });
  
      const request2 = axios.get(`https://app.hhoms.com/api/form/Categories/${folder.id}`, {
        headers: { ApplicationToken: appToken, User: userData.id }
      });
    
      const [response1, response2] = await axios.all([request1, request2]);
 
    
    
      // Now you can handle both responses
      console.log('Response 1:', response1.data);
      console.log('Response 2:', response2.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Klasör adı boş olamaz');
      return;
    }

    // Karakter sınırı kontrolü
    if (newFolderName.trim().length > MAX_FOLDER_NAME_LENGTH) {
      toast.error(`Klasör adı ${MAX_FOLDER_NAME_LENGTH} karakterden uzun olamaz.`);
      return;
    }
    
    if (newFolderName.trim().length < 2) {
      toast.error('Klasör adı en az 2 karakter olmalıdır.');
      return;
    }
    
    // Geçersiz karakterleri kontrol et
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(newFolderName)) {
      toast.error('Klasör adı şu karakterleri içeremez: < > : " / \\ | ? *');
      return;
    }

    const parentId = selectedFolderId || 0;
    console.log("Seçilen Klasör ID:", selectedFolderId);
    console.log("Parent ID:", parentId);
    try {
      const newFolder = {
        parentId: parentId,
        orderByNo: 0,
        title: newFolderName.trim(),
        isActive: true,
      };

      const response = await axios.post('https://app.hhoms.com/api/form/Categories', newFolder, {
        headers: {
          ApplicationToken: appToken,
          User: userData.id,
          'Content-Type': 'application/json',
        }
      });

      console.log("Klasör oluşturma yanıtı:", response.data);
      setFolderDialogOpen(false);
      setNewFolderName('');
      await fetchData(); // Klasör listesini yenile
      toast.success('Klasör başarıyla oluşturuldu');
    } catch (error) {
      console.error('Klasör oluşturma hatası:', error.response?.data || error.message);
      toast.error('Klasör oluşturulurken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  // Alt klasör ekleme işlevi
  const handleAddSubfolder = (parentFolderId) => {
    setSelectedFolderId(parentFolderId); // Seçilen klasörü ayarla
    setNewFolderName(''); // Yeni klasör adını temizle
    setFolderDialogOpen(true); // Klasör oluşturma diyaloğunu aç
  };

  // Klasör düzenleme işlevi
  const handleEditFolder = (folderId, currentName) => {
    setEditingFolderId(folderId);
    setEditFolderName(currentName);
    setEditFolderDialogOpen(true);
  };

  // Klasör güncelleme işlevi
  const handleUpdateFolder = async () => {
    if (!editFolderName.trim()) {
      toast.error('Klasör adı boş olamaz');
      return;
    }

    // Karakter sınırı kontrolü
    if (editFolderName.trim().length > MAX_FOLDER_NAME_LENGTH) {
      toast.error(`Klasör adı ${MAX_FOLDER_NAME_LENGTH} karakterden uzun olamaz.`);
      return;
    }
    
    if (editFolderName.trim().length < 2) {
      toast.error('Klasör adı en az 2 karakter olmalıdır.');
      return;
    }
    
    // Geçersiz karakterleri kontrol et
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(editFolderName)) {
      toast.error('Klasör adı şu karakterleri içeremez: < > : " / \\ | ? *');
      return;
    }

    try {
      // Önce mevcut klasör bilgilerini al
      const folderToUpdate = findFolderById(data.folders, editingFolderId);
      if (!folderToUpdate) {
        toast.error('Güncellenecek klasör bulunamadı');
        return;
      }

      const updateData = {
        id: editingFolderId,
        parentId: folderToUpdate.parentId || 0,
        orderByNo: folderToUpdate.orderByNo || 0,
        title: editFolderName.trim(),
        isActive: folderToUpdate.isActive !== undefined ? folderToUpdate.isActive : true,
      };

      console.log('Güncelleme verisi:', updateData);

      const response = await axios.put(
        `https://app.hhoms.com/api/form/Categories`,
        updateData,
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            'Content-Type': 'application/json',
          }
        }
      );

      console.log("Klasör güncelleme yanıtı:", response.data);
      setEditFolderDialogOpen(false);
      setEditFolderName('');
      setEditingFolderId(null);
      await fetchData(); // Klasör listesini yenile
      toast.success('Klasör başarıyla güncellendi');
    } catch (error) {
      console.error('Klasör güncelleme hatası:', error.response?.data || error.message);
      toast.error('Klasör güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  // Klasör ID'sine göre klasör bulma yardımcı fonksiyonu
  const findFolderById = (folders, id) => {
    for (const folder of folders) {
      if (folder.id === id) {
        return folder;
      }
      if (folder.children && folder.children.length > 0) {
        const found = findFolderById(folder.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Klasör silme işlevi
  const deleteFolder = async (folderId) => {
    try {
      const response = await axios.delete(`https://app.hhoms.com/api/form/Categories/${folderId}`, {
        headers: {
          ApplicationToken: appToken,
          User: userData.id,
        }
      });
      
      console.log("Klasör silme yanıtı:", response.data);
      toast.success("Klasör başarıyla silindi.");
      
      // Eğer silinen klasör seçili klasörse, ana klasöre dön
      if (selectedFolderId === folderId) {
        setSelectedFolderId(0);
        await fetchForms(); // Tüm formları yeniden yükle
      }
      
      await fetchData(); // Klasör listesini yenile
    } catch (error) {
      console.error("Klasör silme hatası:", error.response?.data || error.message);
      toast.error("Klasör silme işlemi sırasında hata oluştu: " + (error.response?.data?.message || error.message));
    }
  };

  // Form taşıma işlevi
  const handleMoveToFolder = async (formId, folderId) => {
    console.log(`Form taşınıyor: FormID=${formId}, FolderID=${folderId}`);
    
    if (!formId) {
      console.error("Form ID boş veya tanımsız");
      toast.error("Form taşınamadı: Form ID bulunamadı");
      return false;
    }
    
    try {
      // Form taşıma işlemi için API isteği - Yöntem 1
      const response = await axios.put(
        `https://app.hhoms.com/api/form/Libraries/parent`,
        { 
          formId: formId,
          categoryId: folderId
        },
        {
          headers: {
            ApplicationToken: appToken,
            User: userData.id,
            "Content-Type": "application/json",
          },
          timeout: 10000
        }
      );

      // Yanıtı kontrol et
      if (response.status === 200 || response.status === 201) {
        console.log("Form taşıma başarılı:", response.data);
        toast.success("Form başarıyla taşındı");
        
        // Formları yeniden yükle
        if (selectedFolderId) {
          await handleFolderSelect({ id: selectedFolderId });
        } else {
          await fetchForms();
        }
        return true;
      } else {
        throw new Error("Beklenmeyen yanıt kodu: " + response.status);
      }
    } catch (error) {
      console.error("Form taşıma hatası (Yöntem 1):", error);
      
      // Alternatif endpoint deneyelim - Yöntem 2
      try {
        console.log("Alternatif taşıma yöntemi deneniyor...");
        
        // Güncellenmiş form verisi oluştur
        const updatedForm = {
          id: formId,
          parentId: folderId
        };
        
        // Form güncelleme isteği gönder
        const response = await axios.put(
          `https://app.hhoms.com/api/form/Libraries/${formId}`,
          updatedForm,
          {
            headers: {
              ApplicationToken: appToken,
              User: userData.id,
              "Content-Type": "application/json",
            },
            timeout: 10000
          }
        );
        
        if (response.status === 200 || response.status === 201) {
          console.log("Alternatif form taşıma başarılı:", response.data);
          toast.success("Form başarıyla taşındı");
          
          // Formları yeniden yükle
          if (selectedFolderId) {
            await handleFolderSelect({ id: selectedFolderId });
          } else {
            await fetchForms();
          }
          return true;
        }
      } catch (altError) {
        console.error("Alternatif form taşıma hatası (Yöntem 2):", altError);
        
        // Üçüncü yöntem deneyelim - Yöntem 3
        try {
          console.log("Üçüncü taşıma yöntemi deneniyor...");
          
          // Basit veri yapısı ile deneyelim
          const response = await axios.put(
            `https://app.hhoms.com/api/form/Libraries`,
            { 
              id: formId,
              parentId: folderId
            },
            {
              headers: {
                ApplicationToken: appToken,
                User: userData.id,
                "Content-Type": "application/json",
              },
              timeout: 10000
            }
          );
          
          if (response.status === 200 || response.status === 201) {
            console.log("Üçüncü form taşıma başarılı:", response.data);
            toast.success("Form başarıyla taşındı");
            
            // Formları yeniden yükle
            if (selectedFolderId) {
              await handleFolderSelect({ id: selectedFolderId });
            } else {
              await fetchForms();
            }
            return true;
          }
        } catch (thirdError) {
          console.error("Üçüncü form taşıma hatası (Yöntem 3):", thirdError);
        }
      }
      
      // Hata detaylarını daha ayrıntılı logla
      if (error.response) {
        console.error("Sunucu yanıtı:", error.response.data);
        console.error("Durum kodu:", error.response.status);
        toast.error(`Form taşınırken hata oluştu: ${error.response.status} - ${error.response.data?.message || 'Sunucu hatası'}`);
      } else if (error.request) {
        console.error("İstek yapıldı ama yanıt alınamadı:", error.request);
        toast.error("Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.");
      } else {
        console.error("İstek hatası:", error.message);
        toast.error(`Form taşınırken hata oluştu: ${error.message}`);
      }
      
      return false;
    }
  };

  // Seçilen formları klasöre taşıma işlevi
  const handleAssignSelectedForms = async () => {
    if (!selectedFolderId) {
      toast.warn("Lütfen bir klasör seçin.");
      return;
    }

    if (selectedForms.length === 0) {
      toast.warn("Lütfen en az bir form seçin.");
      return;
    }

    setLoading(true);
    try {
      console.log(`${selectedForms.length} form ${selectedFolderId} klasörüne taşınıyor...`);
      
      // Önce toplu taşıma deneyelim
      try {
        const response = await axios.put(
          `https://app.hhoms.com/api/form/Libraries/moveMultiple`,
          { 
            formIds: selectedForms,
            categoryId: selectedFolderId
          },
          {
            headers: {
              ApplicationToken: appToken,
              User: userData.id,
              "Content-Type": "application/json",
            },
            timeout: 15000
          }
        );
        
        console.log("Toplu taşıma yanıtı:", response.data);
        toast.success(`${selectedForms.length} form başarıyla taşındı.`);
      } catch (bulkError) {
        console.error("Toplu taşıma hatası:", bulkError);
        toast.warn("Toplu taşıma başarısız oldu, formlar tek tek taşınacak.");
        
        // Toplu taşıma başarısız olursa, tek tek taşıma deneyelim
        let successCount = 0;
        for (const formId of selectedForms) {
          const success = await handleMoveToFolder(formId, selectedFolderId);
          if (success) successCount++;
          
          // Her form arasında kısa bir bekleme ekleyelim
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        if (successCount === selectedForms.length) {
          toast.success(`${successCount} form başarıyla taşındı.`);
        } else {
          toast.info(`${successCount}/${selectedForms.length} form taşındı.`);
        }
      }
      
      setSelectedForms([]); // Seçilen formları sıfırla
      
      // Formları yeniden yükle
      if (selectedFolderId) {
        await handleFolderSelect({ id: selectedFolderId });
      } else {
        await fetchForms();
      }
    } catch (error) {
      console.error("Form taşıma hatası:", error);
      
      // Hata detaylarını daha ayrıntılı logla
      if (error.response) {
        console.error("Sunucu yanıtı:", error.response.data);
        toast.error(`Form taşıma işlemi sırasında hata oluştu: ${error.response.data?.message || 'Sunucu hatası'}`);
      } else if (error.request) {
        console.error("İstek yapıldı ama yanıt alınamadı:", error.request);
        toast.error("Sunucudan yanıt alınamadı. Lütfen internet bağlantınızı kontrol edin.");
      } else {
        console.error("İstek hatası:", error.message);
        toast.error(`Form taşıma işlemi sırasında hata oluştu: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectForm = (formId) => {
    console.log("Form seçildi:", formId);
    
    if (!formId) {
      console.error("Form ID bulunamadı");
      return;
    }
    
    // formId'nin bir event nesnesi olup olmadığını kontrol et
    const actualFormId = formId?.target ? formId.target.value : formId;
    
    setSelectedForms(prev => {
      // Eğer form zaten seçiliyse, seçimden kaldır
      if (prev.includes(actualFormId)) {
        console.log(`Form ${actualFormId} seçimden kaldırıldı`);
        return prev.filter(id => id !== actualFormId);
      } 
      // Değilse, seçime ekle
      else {
        console.log(`Form ${actualFormId} seçime eklendi`);
        return [...prev, actualFormId];
      }
    });
  };

  const handleToggleFormStatus = async (formId, currentStatus) => {
    try {
      const response = await axios.put(
        `https://app.hhoms.com/api/form/Libraries/statu/${formId}/${!currentStatus}`,
        null,
        { 
          headers: { 
            ApplicationToken: appToken, 
            User: userData.id,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      if (response.status === 200) {
        // Form listesini güncelle
        setData(prevData => ({
          ...prevData,
          forms: prevData.forms.map(form => 
            (form.id === formId || form.formId === formId) ? { ...form, isActive: !currentStatus } : form
          )
        }));
        
        toast.success(`Form ${!currentStatus ? 'aktif' : 'pasif'} duruma getirildi`);
      }
    } catch (error) {
      console.error("Form durumu güncellenirken hata:", error);
      toast.error("Form durumu güncellenirken bir hata oluştu");
    }
  };

  useEffect(() => {
    const handleFormCopied = (event) => {
      // Yeni form eklendiğinde formları yeniden yükle
      fetchForms();
    };

    window.addEventListener('formCopied', handleFormCopied);
    
    return () => {
      window.removeEventListener('formCopied', handleFormCopied);
    };
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await fetchData(); // Klasörleri getir
        await fetchForms(); // Formları getir
      } catch (error) {
        console.error("Veri yüklenirken hata:", error);
        toast.error("Veriler yüklenirken bir hata oluştu");
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []); // Boş dependency array ile sadece bir kez çalışacak

  return (
    <Container maxWidth={false} sx={{ 
      mt: { xs: 8, sm: 10 },
      mb: 2, 
      px: { xs: 1, sm: 2, md: 3 }, 
      width: '100%',
      maxWidth: '100%',
      height: 'calc(100vh - 140px)',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)',
      borderRadius: '8px',
      position: 'relative',
      top: '20px'
    }}>
      <Grid container spacing={3} sx={{ height: '100%' }}>
        {/* Sol kolon - Klasörler */}
        <Grid item xs={12} sm={4} md={3} lg={3}>
          <Paper sx={{ 
            p: 3, 
            height: '100%',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)'
          }} elevation={0}>
            {/* Klasörler Başlığı ve Yeni Klasör Butonu */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              flexShrink: 0
            }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  color: '#1a202c',
                  fontSize: '1.25rem'
                }}
              >
                Klasörler
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedFolderId(0);
                  setNewFolderName('');
                  setFolderDialogOpen(true);
                }}
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#2563EB'
                  }
                }}
              >
                + Yeni Klasör
              </Button>
            </Box>
            
            {/* Klasör Ağacı */}
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '3px',
                '&:hover': {
                  background: '#a8a8a8',
                },
              },
            }}>
              {loading || refreshing ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <FolderTree 
                  folders={data.folders} 
                  selectedFolderId={selectedFolderId} 
                  onFolderSelect={handleFolderSelect}
                  onDeleteFolder={deleteFolder}
                  onAddFolder={handleAddSubfolder}
                  onEditFolder={handleEditFolder}
                />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Sağ kolon - Tüm Formlar */}
        <Grid item xs={12} sm={8} md={9} lg={9}>
          <Paper sx={{ 
            p: 3,
            height: '100%',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)'
          }} elevation={0}>
            {/* Tüm Formlar Başlığı */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              flexShrink: 0
            }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  color: '#1a202c',
                  fontSize: '1.25rem'
                }}
              >
                Tüm Formlar
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={fetchForms}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: '#3B82F6',
                    color: '#3B82F6',
                    '&:hover': {
                      borderColor: '#2563EB',
                      backgroundColor: 'rgba(59, 130, 246, 0.04)'
                    }
                  }}
                >
                  Yenile
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/form-builder')}
                  sx={{
                    backgroundColor: '#3B82F6',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#2563EB'
                    }
                  }}
                >
                  Yeni Form
                </Button>
              </Box>
            </Box>
            
            {/* Form Kartları */}
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'auto',
              px: 1
            }}> 
              {loading || refreshing ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)'
                  },
                  gap: 2,
                  alignContent: 'flex-start',
                  pt: 1
                }}>
                  {data.forms.map(form => (
                    <Box key={`form-${form.id}`}>
                      <FormCard 
                        form={form}
                        selected={selectedForms.includes(form.id)}
                        onSelect={() => handleSelectForm(form.id)}
                        onEdit={() => navigate(`/form-edit/${form.formId || form.id}`)}
                        onDelete={() => handleDeleteForm(form.id)}
                        onCopy={() => handleDuplicateForm(form)}
                        onShare={() => navigate(`/link/${form.formId || form.id}`)}
                        folders={flattenFolderTree(data.folders)}
                        onToggleStatus={handleToggleFormStatus}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Dialog 
        open={folderDialogOpen} 
        onClose={() => setFolderDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedFolderId ? t('create_subfolder') : t('create_new_folder')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('folder_name')}
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
            helperText={
              <Box component="span">
                <span>{newFolderName.length}/{MAX_FOLDER_NAME_LENGTH} karakter</span>
                {newFolderName.length > MAX_FOLDER_NAME_LENGTH && (
                  <span style={{ color: '#d32f2f', marginLeft: '8px' }}>
                    Maksimum karakter sınırını aştınız
                  </span>
                )}
                {newFolderName.length > 0 && newFolderName.length < 2 && (
                  <span style={{ color: '#d32f2f', marginLeft: '8px' }}>
                    En az 2 karakter olmalıdır
                  </span>
                )}
              </Box>
            }
            error={newFolderName.length > MAX_FOLDER_NAME_LENGTH || (newFolderName.length > 0 && newFolderName.length < 2)}
            InputProps={{
              sx: { backgroundColor: '#fff' }
            }}
          />
          {selectedFolderId > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('subfolder_info', { folderName: data.folders.find(f => f.id === selectedFolderId)?.title || t('selected_folder') })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFolderDialogOpen(false)}>{t('cancel')}</Button>
          <Button 
            onClick={handleCreateFolder} 
            variant="contained"
            disabled={
              !newFolderName.trim() || 
              newFolderName.length > MAX_FOLDER_NAME_LENGTH || 
              newFolderName.length < 2 ||
              /[<>:"/\\|?*]/.test(newFolderName)
            }
          >
            {t('create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Klasör Düzenleme Dialog'u */}
      <Dialog 
        open={editFolderDialogOpen} 
        onClose={() => setEditFolderDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Klasör Adını Düzenle
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Klasör Adı"
            fullWidth
            value={editFolderName}
            onChange={(e) => setEditFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleUpdateFolder();
              }
            }}
            helperText={
              <Box component="span">
                <span>{editFolderName.length}/{MAX_FOLDER_NAME_LENGTH} karakter</span>
                {editFolderName.length > MAX_FOLDER_NAME_LENGTH && (
                  <span style={{ color: '#d32f2f', marginLeft: '8px' }}>
                    Maksimum karakter sınırını aştınız
                  </span>
                )}
                {editFolderName.length > 0 && editFolderName.length < 2 && (
                  <span style={{ color: '#d32f2f', marginLeft: '8px' }}>
                    En az 2 karakter olmalıdır
                  </span>
                )}
              </Box>
            }
            error={editFolderName.length > MAX_FOLDER_NAME_LENGTH || (editFolderName.length > 0 && editFolderName.length < 2)}
            InputProps={{
              sx: { backgroundColor: '#fff' }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFolderDialogOpen(false)}>İptal</Button>
          <Button 
            onClick={handleUpdateFolder} 
            variant="contained"
            disabled={
              !editFolderName.trim() || 
              editFolderName.length > MAX_FOLDER_NAME_LENGTH || 
              editFolderName.length < 2 ||
              /[<>:"/\\|?*]/.test(editFolderName)
            }
          >
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
      />
    </Container>
  );
};

export default FolderManager;